import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { authService } from "services/auth.service";
import { useToastNotification } from "utils/toast-utils";
import { useFormik } from 'formik';
import {
  forgotPasswordStepOneSchema,
  forgotPasswordStepTwoSchema,
  forgotPasswordStepThreeSchema
} from '../schemas';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Input,
  Text,
  useColorModeValue,
  Icon,
  InputRightElement,
  InputGroup
} from "@chakra-ui/react";
// Custom components
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/auth.png";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

function ForgotPassword() {
  const { showToast } = useToastNotification();
  const history = useHistory();
  // Chakra color mode
  const textColor = "#42505C";
  const textColorSecondary = "#404040";
  const textColorDetails = useColorModeValue("#42505C", "secondaryGray.600");
  const textColorBrand = useColorModeValue("#42505C", "white");
  const buttonColor = "#602CE8";

  const [step, setStep] = useState(1);
  const [token, setToken] = useState("");
  const [show, setShow] = useState({
    newPassword: false,
    confirmPassword: false
  });

  const handleShowPassword = (field) => {
    setShow(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const stepOneFormik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: forgotPasswordStepOneSchema,
    onSubmit: async (values) => {
      try {
        const response = await authService.requestPasswordChange(values.email);
        if (response.status) {
          setStep(2);
          showToast('Success', 'OTP sent to your email', 'success');
        }
      } catch (err) {
        showToast('Error', err.response?.data?.message || 'Failed to send OTP', 'error');
      }
    }
  });

  const stepTwoFormik = useFormik({
    initialValues: {
      otp: ['', '', '', '']
    },
    validationSchema: forgotPasswordStepTwoSchema,
    onSubmit: async (values) => {
      try {
        const response = await authService.verifyOtp(stepOneFormik.values.email, values.otp);
        if (response.status) {
          setToken(response.token);
          setStep(3);
          showToast('Success', 'OTP verified successfully', 'success');
        }
      } catch (err) {
        showToast('Error', err.response?.data?.message || 'Invalid OTP', 'error');
      }
    }
  });

  const stepThreeFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: forgotPasswordStepThreeSchema,
    onSubmit: async (values) => {
      try {
        const response = await authService.changePassword(
          stepOneFormik.values.email,
          token,
          values.newPassword
        );
        if (response.status) {
          showToast('Success', 'Password changed successfully', 'success');
          history.push("/auth/sign-in");
        }
      } catch (err) {
        showToast('Error', err.response?.data?.message || 'Failed to change password', 'error');
      }
    }
  });

  const handleOtpChange = (index, value) => {
    const newOtp = [...stepTwoFormik.values.otp];
    
    // Handle paste event
    if (value.length > 1) {
      const pastedDigits = value.split('').slice(0, 4);
      pastedDigits.forEach((digit, i) => {
        newOtp[i] = digit;
      });
      stepTwoFormik.setFieldValue('otp', newOtp);
      
      // Focus the last input if all digits are filled
      if (pastedDigits.length === 4) {
        const lastInput = document.querySelector(`input[name="otp-3"]`);
        if (lastInput) lastInput.focus();
      }
      return;
    }

    // Handle single digit input
    newOtp[index] = value;
    stepTwoFormik.setFieldValue('otp', newOtp);
    
    // Move to next input if a digit was entered
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[name="otp-${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
    
    // Move to previous input if backspace was pressed and current input is empty
    if (!value && index > 0) {
      const prevInput = document.querySelector(`input[name="otp-${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading
            color={textColor}
            fontSize="54px"
            fontWeight="700"
            mb="20px"
            fontFamily="'Roboto', sans-serif"
          >
            {step === 1
              ? "Forgot password?"
              : step === 2
              ? "Enter OTP"
              : "Change password"}
          </Heading>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          {step === 1 ? (
            <FormControl 
              as="form" 
              onSubmit={stepOneFormik.handleSubmit}
              isInvalid={stepOneFormik.touched.email && stepOneFormik.errors.email}
            >
              <Text
                mb="36px"
                ms="4px"
                color={textColorSecondary}
                fontWeight="400"
                fontSize="14px"
              >
                Don't worry we can help you out! if you still remember your
                email address you can quickly reset your password. Just input
                that information in the fields below and click on the button.
                This will send you a new email that will link you to the
                password change Page.{" "}
              </Text>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={"#333333"}
                mb="8px"
              >
                Email address
              </FormLabel>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: "0px", md: "0px" }}
                type="email"
                placeholder="john@smith.com"
                mb="24px"
                name="email"
                value={stepOneFormik.values.email}
                onChange={stepOneFormik.handleChange}
                onBlur={stepOneFormik.handleBlur}
                width={{ md: "472.42523193359375px" }}
                height="48px"
                borderRadius="6px"
                borderWidth="1px"
              />
              <FormErrorMessage mb="24px">{stepOneFormik.errors.email}</FormErrorMessage>
              <Button
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                width={{ md: "472.42523193359375px" }}
                h="50"
                mb="24px"
                background={buttonColor}
                borderRadius="6px"
                borderWidth="1px"
                onClick={stepOneFormik.handleSubmit}
                isDisabled={!stepOneFormik.isValid || stepOneFormik.isSubmitting}
                _hover={{ bg: "#5835A8" }}
              >
                Request password change
              </Button>
            </FormControl>
          ) : step === 2 ? (
            <FormControl 
              as="form" 
              onSubmit={stepTwoFormik.handleSubmit}
              isInvalid={stepTwoFormik.touched.otp && stepTwoFormik.errors.otp}
            >
              <Text mb="4" color={textColorSecondary}>
                Input the OTP Code sent to you over email
              </Text>
              <Flex gap={4} mb={6}>
                {stepTwoFormik.values.otp.map((digit, index) => (
                  <Input
                    key={index}
                    name={`otp-${index}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedData = e.clipboardData.getData('text');
                      handleOtpChange(0, pastedData);
                    }}
                    maxLength={1}
                    w="50px"
                    h="50px"
                    textAlign="center"
                    fontSize="lg"
                    mx="auto"
                    borderRadius="6px"
                    borderWidth="1px"
                  />
                ))}
              </Flex>
              <FormErrorMessage mb={6}>
                {typeof stepTwoFormik.errors.otp === 'string' 
                  ? stepTwoFormik.errors.otp 
                  : 'Please enter all 4 digits of the OTP'}
              </FormErrorMessage>
              <Button
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                background={buttonColor}
                borderRadius="6px"
                borderWidth="1px"
                onClick={stepTwoFormik.handleSubmit}
                isDisabled={!stepTwoFormik.isValid || stepTwoFormik.isSubmitting}
                _hover={{ bg: "#5835A8" }}
              >
                Verify OTP
              </Button>
            </FormControl>
          ) : (
            <FormControl 
              as="form" 
              onSubmit={stepThreeFormik.handleSubmit}
              isInvalid={stepThreeFormik.touched.newPassword && stepThreeFormik.errors.newPassword}
            >
              <Text
                mb="36px"
                ms="4px"
                color={textColorSecondary}
                fontWeight="400"
                fontSize="14px"
              >
                Input your new desired password in the input fields below to
                create a new password. We strongly advise you to store it
                safely.
              </Text>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={"#333333"}
                mb="8px"
              >
                New password
              </FormLabel>
              <InputGroup>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  type={show.newPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  mb="24px"
                  name="newPassword"
                  value={stepThreeFormik.values.newPassword}
                  onChange={stepThreeFormik.handleChange}
                  onBlur={stepThreeFormik.handleBlur}
                  height="48px"
                  borderRadius="6px"
                  borderWidth="1px"
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show.newPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => handleShowPassword('newPassword')}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage mb="24px">{stepThreeFormik.errors.newPassword}</FormErrorMessage>

              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={"#333333"}
                mb="8px"
              >
                Confirm new password
              </FormLabel>
              <InputGroup>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  type={show.confirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  mb="24px"
                  name="confirmPassword"
                  value={stepThreeFormik.values.confirmPassword}
                  onChange={stepThreeFormik.handleChange}
                  onBlur={stepThreeFormik.handleBlur}
                  height="48px"
                  borderRadius="6px"
                  borderWidth="1px"
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show.confirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => handleShowPassword('confirmPassword')}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage mb="24px">{stepThreeFormik.errors.confirmPassword}</FormErrorMessage>

              <Button
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                background={buttonColor}
                borderRadius="6px"
                borderWidth="1px"
                onClick={stepThreeFormik.handleSubmit}
                isDisabled={!stepThreeFormik.isValid || stepThreeFormik.isSubmitting}
                _hover={{ bg: "#5835A8" }}
              >
                Change password
              </Button>
            </FormControl>
          )}
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            maxW="100%"
            mt={{ base: "0px", md: "24px" }}
          >
            <Text
              color={textColorDetails}
              fontWeight="400"
              fontSize="14px"
              mb={{ base: "0px", md: "16px" }}
            >
              Do you need help?
            </Text>
            <Text
              color={buttonColor}
              as="span"
              ms="5px"
              fontWeight="500"
              cursor="pointer"
            >
              Customer support
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default ForgotPassword;
